import { FC, useEffect, useRef, KeyboardEvent } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { IconButton } from '@spotnana/blocks/src/IconButton';
import useCarouselOffset from '../../../hooks/useCarouselOffset';
import useResizeObserver from '../../../hooks/useResizeObserver';
import {
  cards_container,
  carousel_container,
  carousel_footer,
  carousel_header,
  footer_arrow_btn,
  footer_arrow_wrapper,
} from './CardCarousel.styles';

const CARD_WIDTH = 232;

interface CardCarouselProps {
  children: React.ReactNode;
  showArrows?: boolean;
  cardsCount: number;
  currentTab?: string;
  actions?: React.ReactNode;
  header?: React.ReactNode;
  cardWidth?: number;
  placement?: 'top' | 'bottom';
  minCardCount?: number;
  allowFolding?: boolean;
}

const CardCarousel: FC<CardCarouselProps> = ({
  children,
  showArrows = true,
  cardsCount,
  currentTab,
  actions,
  header,
  cardWidth = CARD_WIDTH,
  placement = 'bottom',
  minCardCount = 3,
  allowFolding = true,
}) => {
  const { t: tt } = useTranslation('WEB');
  const cardCarouselContainerRef = useRef<HTMLDivElement>(null);
  const { width: cardCarouselContainerWidth } = useResizeObserver<HTMLDivElement>(cardCarouselContainerRef);

  const { offset, increaseOffset, decreaseOffset, resetOffset, maxOffset } = useCarouselOffset({
    itemWidth: cardWidth,
    itemCount: cardsCount,
    containerWidth: cardCarouselContainerWidth,
    allowFolding,
  });

  // reset offset if tab is switched
  useEffect(() => resetOffset(), [currentTab, resetOffset]);

  const leftArrowProps = {
    onClick: decreaseOffset,
    onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        decreaseOffset();
      }
    },
    'data-testid': 'payment-selector-left-arrow',
    'aria-label': tt('Scroll to the left'),
  };

  const rightArrowProps = {
    onClick: increaseOffset,
    onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        increaseOffset();
      }
    },
    'data-testid': 'payment-selector-right-arrow',
    'aria-label': tt('Scroll to the right'),
  };

  const CarouselBtns = (
    <div css={footer_arrow_wrapper}>
      <div css={footer_arrow_btn} {...leftArrowProps}>
        <IconButton
          disabled={!allowFolding && offset === 0}
          icon="ChevronLeft"
          tooltip={tt('Scroll to the left')}
          size="xlarge"
        />
      </div>
      <div css={footer_arrow_btn} {...rightArrowProps}>
        <IconButton
          disabled={!allowFolding && offset === maxOffset}
          icon="ChevronRight"
          tooltip={tt('Scroll to the right')}
          size="xlarge"
        />
      </div>
    </div>
  );

  return (
    <div ref={cardCarouselContainerRef} css={carousel_container} data-testid="payment-cards-container">
      {header && (
        <div css={carousel_header}>
          {header}
          {placement === 'top' && showArrows && cardsCount > minCardCount && CarouselBtns}
        </div>
      )}
      {cardsCount > 0 && (
        <div css={cards_container} style={{ marginLeft: offset * -1, width: cardsCount * cardWidth }}>
          {children}
        </div>
      )}
      <div css={carousel_footer}>
        {actions}
        {placement === 'bottom' && showArrows && cardsCount > minCardCount && CarouselBtns}
      </div>
    </div>
  );
};

export default CardCarousel;
